import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Field, Input, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import { fetchClientIp, getAccessToken } from '@/auth/session'
import '@/styles/app.css'

export function LoginPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void fetchClientIp()
    const token = getAccessToken()
    if (!token) return
    sessionApi
      .getCurrentUser()
      .then((res) => {
        if (res.status === 200) navigate('/sessionmarket', { replace: true })
      })
      .catch(() => {})
  }, [navigate])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast.error('Email and password required')
      return
    }
    setBusy(true)
    try {
      const clientIP = localStorage.getItem('clientIP')
      const loginData = await sessionApi.adminLogin({ email, password, clientIP })
      const body = loginData.data as {
        value?: boolean
        msg?: string
        data?: { accessToken?: string[]; userData?: unknown; is2faEnabled?: boolean }
        accessToken?: string[]
        userData?: unknown
        is2faEnabled?: boolean
      }
      const payload = (body?.data ?? body) as {
        accessToken?: string[]
        userData?: unknown
        is2faEnabled?: boolean
      }
      if (body?.msg === 'Incorrect email or password' || body?.value === false) {
        toast.error(body.msg || 'Incorrect email or password')
      } else if (loginData.status === 204) {
        toast.error('Your email is not registered or Your account is Deactivated')
      } else if (payload && typeof payload === 'object') {
        if (payload.is2faEnabled) {
          toast.error('2FA required — not supported in this UI yet')
          return
        }
        if (payload.accessToken?.[0]) {
          localStorage.setItem('accessToken', payload.accessToken[0])
          if (payload.userData != null) {
            localStorage.setItem(
              'userDetails',
              typeof payload.userData === 'string' ? payload.userData : JSON.stringify(payload.userData),
            )
          }
          toast.success('Logged in successfully')
          navigate('/sessionmarket')
        } else {
          navigate('/')
        }
      } else {
        navigate('/')
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Login failed'
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sm-login">
      <div className="sm-login-bg" aria-hidden="true" />
      <div className="sm-login-card">
        <div className="sm-login-brand">
          <img src="/brand/strix-logo.png" alt="" width={40} height={40} decoding="async" />
          <div>
            <strong>Session Market</strong>
            <span>Ops console</span>
          </div>
        </div>
        <h1>Sign in</h1>
        <p className="sm-login-lede">Enter your credentials to continue</p>
        <form className="sm-login-form" onSubmit={(e) => void onSubmit(e)}>
          <Field label="Username" required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Username"
                autoComplete="username"
                required
              />
            )}
          </Field>
          <Field label="Password" required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                required
              />
            )}
          </Field>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Login'}
          </Button>
        </form>
      </div>
    </div>
  )
}
